// Shared bundle price math — mirrors backend `services` logic in `bundles.py`
// so the price shown to the customer always matches what checkout will charge.
import { priceForSize, stockForVariant } from "./variant";

// Gusti the admin allows on a kit line (falls back to the single default_flavor
// for kits saved before multi-flavor existed), keeping only those in stock for
// the formato currently in play (the chosen one, or the admin's default).
export const kitFlavorOptions = (item, product, size) => {
  const allowed = (item?.allowed_flavors || []).filter(Boolean);
  const list = allowed.length ? allowed : [item?.default_flavor].filter(Boolean);
  const sz = size ?? item?.default_size ?? "";
  return list.filter((f) => {
    const left = stockForVariant(product?.variant_stock, f, sz);
    return left === undefined || Number(left) > 0;
  });
};

// Formati the admin allows on a kit line (falls back to the single default_size
// for kits saved before multi-size existed), keeping only those in stock for
// the gusto currently in play. NOTE: the formato never moves the kit price —
// see `bundlePricing` below — it only decides what actually ships.
export const kitSizeOptions = (item, product, flavor) => {
  const allowed = (item?.allowed_sizes || []).filter(Boolean);
  const list = allowed.length ? allowed : [item?.default_size].filter(Boolean);
  const fl = flavor ?? item?.default_flavor ?? "";
  return list.filter((s) => {
    const left = stockForVariant(product?.variant_stock, fl, s);
    return left === undefined || Number(left) > 0;
  });
};

export const bundleRatio = (bundle, productsById) => {
  if (bundle.discount_type === "percentage") {
    return Math.max(0, 1 - (Number(bundle.discount_value) || 0) / 100);
  }
  const defaultTotal = (bundle.items || []).reduce((acc, it) => {
    const p = productsById[it.product_id];
    if (!p) return acc;
    return acc + priceForSize(p, it.default_size) * (it.quantity || 1);
  }, 0);
  if (defaultTotal <= 0) return 1;
  return Math.max(0, Math.min(1, (Number(bundle.discount_value) || 0) / defaultTotal));
};

// Resolves each bundle line against the gusto AND formato chosen by the customer
// among the allowed, in-stock options. Price is ALWAYS anchored to the admin's
// default_size (fixed-price kit policy) — whichever formato ships, the kit total
// never moves, so a `fixed_price` kit always costs exactly the admin's price.
export const bundlePricing = (bundle, productsById, flavorChoices = {}, sizeChoices = {}) => {
  const ratio = bundleRatio(bundle, productsById);
  let originalTotal = 0;
  const lines = (bundle.items || []).map((it) => {
    const product = productsById[it.product_id];
    const sizeOptions = kitSizeOptions(it, product, flavorChoices[it.product_id]);
    const chosenSize = sizeChoices[it.product_id];
    const size = sizeOptions.includes(chosenSize) ? chosenSize : (sizeOptions[0] ?? it.default_size ?? "");
    const flavorOptions = kitFlavorOptions(it, product, size);
    const chosenFlavor = flavorChoices[it.product_id];
    const flavor = flavorOptions.includes(chosenFlavor) ? chosenFlavor : (flavorOptions[0] ?? it.default_flavor ?? "");
    const quantity = it.quantity || 1;
    // Pricing basis is always the admin's default_size, never the chosen one.
    const unitPrice = product ? priceForSize(product, it.default_size ?? "") : 0;
    originalTotal += unitPrice * quantity;
    return { product, item: it, flavor, size, quantity, unitPrice };
  });
  originalTotal = Math.round(originalTotal * 100) / 100;
  // "Prezzo fisso": show exactly the figure the admin typed, never a rounded ratio.
  const discountedTotal = bundle.discount_type === "fixed_price"
    ? Math.round((Number(bundle.discount_value) || 0) * 100) / 100
    : Math.round(originalTotal * ratio * 100) / 100;
  const savings = Math.max(0, Math.round((originalTotal - discountedTotal) * 100) / 100);
  return { lines, originalTotal, discountedTotal, savings, ratio };
};
